import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Label } from '@/common/ui/label'
import { Badge } from '@/common/ui/badge'
import { AlertCircle, Clock, Power, PowerOff } from 'lucide-react'
import { format, formatDistanceToNow } from 'date-fns'
import { th } from 'date-fns/locale'
import {
	useMutationSetTemporaryDisable,
	useMutationClearTemporaryDisable,
} from '../../api/hooks'
import { transactionSettingsKeys } from '../../api/keys'
import type { TemporaryDisableInfo, TransactionType } from '../../types'

interface TemporaryDisableCardProps {
	type: TransactionType
	temporaryDisable: TemporaryDisableInfo
	isCurrentlyDisabled: boolean
}

export const TemporaryDisableCard = ({
	type,
	temporaryDisable,
	isCurrentlyDisabled,
}: TemporaryDisableCardProps) => {
	const [durationMinutes, setDurationMinutes] = useState<string>('60')
	const [timeLeft, setTimeLeft] = useState<string>('')
	const queryClient = useQueryClient()

	const typeLabel = type === 'deposit' ? 'ฝากเงิน' : 'ถอนเงิน'

	const setDisableMutation = useMutationSetTemporaryDisable({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: transactionSettingsKeys.status(type),
			})
			toast.success(`ปิด${typeLabel}ชั่วคราวสำเร็จ`)
		},
		onError: () => {
			toast.error(`เกิดข้อผิดพลาดในการปิด${typeLabel}`)
		},
	})

	const clearDisableMutation = useMutationClearTemporaryDisable({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: transactionSettingsKeys.status(type),
			})
			toast.success(`เปิด${typeLabel}สำเร็จ`)
		},
		onError: () => {
			toast.error(`เกิดข้อผิดพลาดในการเปิด${typeLabel}`)
		},
	})

	// Update countdown timer
	useEffect(() => {
		if (!temporaryDisable.isActive || !temporaryDisable.expiresAt) {
			setTimeLeft('')
			return
		}

		const updateTimeLeft = () => {
			const expiresAt = new Date(temporaryDisable.expiresAt!)
			const now = new Date()

			if (expiresAt <= now) {
				setTimeLeft('หมดเวลาแล้ว')
				queryClient.invalidateQueries({
					queryKey: transactionSettingsKeys.status(type),
				})
				return
			}

			setTimeLeft(
				formatDistanceToNow(expiresAt, { locale: th, addSuffix: true }),
			)
		}

		updateTimeLeft()
		const interval = setInterval(updateTimeLeft, 1000)

		return () => clearInterval(interval)
	}, [temporaryDisable.isActive, temporaryDisable.expiresAt, queryClient, type])

	const handleSetDisable = () => {
		const minutes = parseInt(durationMinutes, 10)
		if (isNaN(minutes) || minutes < 1 || minutes > 1440) {
			toast.error('กรุณาระบุเวลาระหว่าง 1-1440 นาที')
			return
		}

		setDisableMutation.mutate({
			type,
			data: { durationMinutes: minutes },
		})
	}

	const handleClearDisable = () => {
		clearDisableMutation.mutate({ type })
	}

	return (
		<Card>
			<CardHeader>
				<div className="flex items-center justify-between">
					<div>
						<CardTitle className="flex items-center gap-2">
							{temporaryDisable.isActive ? (
								<PowerOff className="h-5 w-5 text-red-500" />
							) : (
								<Power className="h-5 w-5 text-green-500" />
							)}
							สถานะ{typeLabel}ชั่วคราว
						</CardTitle>
						<CardDescription>
							ปิด{typeLabel}แบบชั่วคราว (จะเปิดอัตโนมัติเมื่อหมดเวลา)
						</CardDescription>
					</div>
					<Badge variant={isCurrentlyDisabled ? 'destructive' : 'default'}>
						{isCurrentlyDisabled ? 'ปิดอยู่' : 'เปิดอยู่'}
					</Badge>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				{temporaryDisable.isActive && temporaryDisable.expiresAt ? (
					<div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
						<div className="flex items-center gap-2 text-red-600 dark:text-red-400">
							<AlertCircle className="h-5 w-5" />
							<span className="font-medium">ปิด{typeLabel}ชั่วคราวอยู่</span>
						</div>
						<div className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
							<Clock className="h-4 w-4" />
							<span>
								หมดเวลา:{' '}
								{format(
									new Date(temporaryDisable.expiresAt),
									'dd MMM yyyy HH:mm:ss',
									{ locale: th },
								)}
							</span>
						</div>
						{timeLeft && (
							<div className="mt-1 text-sm text-red-600 dark:text-red-400">
								เหลือเวลาอีก {timeLeft}
							</div>
						)}
						<Button
							variant="outline"
							className="mt-4"
							onClick={handleClearDisable}
							disabled={clearDisableMutation.isPending}
						>
							{clearDisableMutation.isPending
								? 'กำลังดำเนินการ...'
								: `เปิด${typeLabel}ทันที`}
						</Button>
					</div>
				) : (
					<div className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="duration">ระยะเวลาที่ต้องการปิด (นาที)</Label>
							<div className="flex gap-2">
								<Input
									id="duration"
									type="number"
									min={1}
									max={1440}
									value={durationMinutes}
									onChange={(e) => setDurationMinutes(e.target.value)}
									placeholder="60"
									className="w-32"
								/>
								<Button
									variant="destructive"
									onClick={handleSetDisable}
									disabled={setDisableMutation.isPending}
								>
									{setDisableMutation.isPending
										? 'กำลังดำเนินการ...'
										: `ปิด${typeLabel}ชั่วคราว`}
								</Button>
							</div>
							<p className="text-sm text-muted-foreground">
								ระบุเวลา 1-1440 นาที (สูงสุด 24 ชั่วโมง)
							</p>
						</div>
						<div className="flex gap-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => setDurationMinutes('30')}
							>
								30 นาที
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setDurationMinutes('60')}
							>
								1 ชั่วโมง
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setDurationMinutes('120')}
							>
								2 ชั่วโมง
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setDurationMinutes('360')}
							>
								6 ชั่วโมง
							</Button>
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	)
}
